import type { TextVariableDto } from '@/entities/variables/model/dtos'
import type { VariableNodeAttributes } from './VariableNode'

import { useAtomValue } from 'jotai'
import { useEffect } from 'react'

import { variablesAtom } from '@/entities/variables'
import { variablesExtensionConfig } from './variables.config'

import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import { RundownPermission } from '@rundown-studio/types'
import { modalActions } from '@/features/rundown/modals/modal.store'

import { NodeViewWrapper } from '@tiptap/react'
import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '../tiptap-helpers'
import {
  Popover,
  PopoverAction,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { PencilIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function VariableNodeViewLive({
  node,
  updateAttributes,
  HTMLAttributes,
  ref,
}: NodeViewLiveProps<HTMLSpanElement>) {
  const attrs = node.attrs as VariableNodeAttributes

  const variable = useGetVariable(attrs)

  useEffect(() => {
    if (variable) {
      updateAttributes({
        fallbackValue: variable.value,
      })
    }
  }, [variable, updateAttributes])

  return (
    <NodeViewWrapper
      as="span"
      // Spread node data-* attrs
      {...HTMLAttributes}
    >
      <VariableNodeComponent node={node} ref={ref} />
    </NodeViewWrapper>
  )
}

export function VariableNodeViewStatic({ node }: NodeViewStaticProps) {
  return <VariableNodeComponent node={node} />
}

function VariableNodeComponent({
  node,
  ...props
}: NodeComponentProps & React.ComponentProps<'span'>) {
  const attrs = node.attrs as VariableNodeAttributes

  const char = variablesExtensionConfig.character
  const variable = useGetVariable(attrs)

  const hasWriteAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  return (
    <Popover>
      <PopoverTrigger
        nativeButton={false}
        tabIndex={-1}
        render={
          <span className="inline-block cursor-help hover:underline" {...props}>
            <span className="opacity-50 text-[11px]">{char}</span>
            {variable.value}
          </span>
        }
      />
      <PopoverContent initialFocus={false}>
        <PopoverHeader>
          <PopoverTitle>Text Variable</PopoverTitle>

          <PopoverAction>
            <Button
              aria-label="Edit variable"
              variant="ghost"
              size="icon-sm"
              disabled={!hasWriteAccess}
              onClick={() => modalActions.open('VARIABLES', {})}
            >
              <PencilIcon />
            </Button>
          </PopoverAction>
        </PopoverHeader>

        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
          <dt className="text-muted-foreground">Name:</dt>
          <dd className="font-mono text-foreground break-all">
            {char}
            {variable.key}
          </dd>

          <dt className="text-muted-foreground">Value:</dt>
          <dd className="font-mono text-foreground break-all">
            {variable.value}
          </dd>
        </dl>
      </PopoverContent>
    </Popover>
  )
}

function useGetVariable(attrs: VariableNodeAttributes) {
  const variables = useAtomValue(variablesAtom)

  return {
    key: attrs.variableKey,
    value: variables[attrs.variableKey] ?? attrs.fallbackValue,
  } satisfies TextVariableDto
}
